// ─────────────────────────────────────────────────────────────────────────────
// IgnoreCodesEffects
// ─────────────────────────────────────────────────────────────────────────────
// NgRx effects for ignored code operations.
// Responsibilities:
//   - Load paginated ignore codes list from API
//   - Delete and bulk-delete ignore codes
//   - Manage loading state via SetIgnoreCodesLoader
//   - Show toast notifications on success/error
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { APIResponse } from 'src/app/core/models/api-response';
import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { CommonService } from 'src/app/shared/common.service';
import { TranslateService } from '@ngx-translate/core';
import { IgnoreCodeService } from '../services/ignore-code.service';
import { IIgnoreCode } from '../models/ignore-code.models';
import * as ignoreCodesActions from './ignore-codes.actions';

@Injectable()
export class IgnoreCodesEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private ignoreCodeService: IgnoreCodeService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  // ── Load ignore codes ────────────────────────────────────────────────────

  LoadIgnoreCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ignoreCodesActions.LoadIgnoreCodes),
      switchMap((action) =>
        this.ignoreCodeService.listIgnoreCodes(action.page, action.key, action.filters, action.sortBy, action.sortMode).pipe(
          map((res: APIResponse<IIgnoreCode>) => ignoreCodesActions.LoadIgnoreCodesSuccess({ response: res })),
          catchError(() => of(ignoreCodesActions.LoadIgnoreCodesError())),
        ),
      ),
    ),
  );

  // ── Create ignore code ───────────────────────────────────────────────────

  CreateIgnoreCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ignoreCodesActions.CreateIgnoreCode),
      switchMap((action) =>
        this.ignoreCodeService.createIgnoreCode(action.payload).pipe(
          map((res: APIResponse<IIgnoreCode>) => ignoreCodesActions.CreateIgnoreCodeSuccess({ ignoreCode: res.data[0] })),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.ignore_codes.toast.create_success'), 'SUCCESS');
          }),
          catchError((error: HttpErrorResponse) => {
            const message = this.resolveErrorMessage(error);
            return of(ignoreCodesActions.CreateIgnoreCodeError({ error: message }));
          }),
        ),
      ),
    ),
  );

  // ── Update ignore code ───────────────────────────────────────────────────

  UpdateIgnoreCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ignoreCodesActions.UpdateIgnoreCode),
      switchMap((action) =>
        this.ignoreCodeService.updateIgnoreCode(action.id, action.payload).pipe(
          map((res: APIResponse<IIgnoreCode>) => ignoreCodesActions.UpdateIgnoreCodeSuccess({ ignoreCode: res.data[0] })),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.ignore_codes.toast.update_success'), 'SUCCESS');
          }),
          catchError((error: HttpErrorResponse) => {
            const message = this.resolveErrorMessage(error);
            return of(ignoreCodesActions.UpdateIgnoreCodeError({ error: message }));
          }),
        ),
      ),
    ),
  );

  // ── Bulk delete ignore codes ─────────────────────────────────────────────

  BulkDeleteIgnoreCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ignoreCodesActions.BulkDeleteIgnoreCodes),
      tap(() => {
        this.store.dispatch(ignoreCodesActions.SetIgnoreCodesLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.ignoreCodeService.bulkDeleteIgnoreCodes(action.ids).pipe(
          map(() => ignoreCodesActions.BulkDeleteIgnoreCodesSuccess()),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('knowledge_base.ignore_codes.toast.bulk_delete_success'), 'SUCCESS');
          }),
          catchError(() => of(ignoreCodesActions.BulkDeleteIgnoreCodesError())),
          finalize(() => {
            this.store.dispatch(ignoreCodesActions.SetIgnoreCodesLoader({ isLoading: false }));
          }),
        ),
      ),
    ),
  );

  // ── Helpers ──────────────────────────────────────────────────────────────

  private resolveErrorMessage(error: HttpErrorResponse): string {
    const status = error?.status;
    const backendMessage = error?.error?.message || error?.message || '';

    if (status === 409) {
      return this.translateService.instant('knowledge_base.ignore_codes.toast.duplicate');
    }
    if (status === 400 && /cannot be edited|order-sourced/i.test(backendMessage)) {
      return this.translateService.instant('knowledge_base.ignore_codes.toast.order_sourced_read_only');
    }
    return backendMessage || this.translateService.instant('knowledge_base.ignore_codes.toast.error');
  }
}
